import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";
import { logoutErrorMessage } from "../../../../../Helpers/ErrorMessages";
import { dateConvert } from "../../../../Utils/ConvertDate";

/**
 * Constants for ANC Booking
 */
const BLOOD_GROUP_OPTIONS = [
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
];

const MARITAL_STATUS_OPTIONS = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
    { value: "separated", label: "Separated" },
    { value: "cohabiting", label: "Cohabiting" },
];

const EDUCATION_LEVEL_OPTIONS = [
    { value: "none", label: "None" },
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary (O-Level)" },
    { value: "a_level", label: "Advanced (A-Level)" },
    { value: "certificate", label: "Certificate" },
    { value: "diploma", label: "Diploma" },
    { value: "degree", label: "Degree" },
    { value: "postgraduate", label: "Postgraduate" },
];

const RELATIONSHIP_OPTIONS = [
    { value: "spouse", label: "Spouse" },
    { value: "parent", label: "Parent" },
    { value: "sibling", label: "Sibling" },
    { value: "child", label: "Child" },
    { value: "friend", label: "Friend" },
    { value: "other", label: "Other" },
];

const MEDICAL_HISTORY_CHECKLIST = [
    { key: "hypertension", label: "Hypertension" },
    { key: "diabetes", label: "Diabetes Mellitus" },
    { key: "heart_disease", label: "Heart Disease" },
    { key: "sickle_cell", label: "Sickle Cell Disease" },
    { key: "asthma", label: "Asthma" },
    { key: "epilepsy", label: "Epilepsy" },
    { key: "hiv", label: "HIV/AIDS" },
    { key: "tuberculosis", label: "Tuberculosis" },
    { key: "kidney_disease", label: "Kidney Disease" },
    { key: "thyroid_disease", label: "Thyroid Disease" },
    { key: "mental_illness", label: "Mental Illness" },
    { key: "bleeding_disorder", label: "Bleeding Disorder" },
];

const SURGICAL_HISTORY_CHECKLIST = [
    { key: "cesarean_section", label: "Previous Cesarean Section" },
    { key: "myomectomy", label: "Myomectomy" },
    { key: "appendectomy", label: "Appendectomy" },
    { key: "laparotomy", label: "Laparotomy" },
    { key: "cervical_cerclage", label: "Cervical Cerclage" },
    { key: "dilation_curettage", label: "Dilation & Curettage (D&C)" },
    { key: "other_surgery", label: "Other Surgery" },
];

const EMPTY_PREGNANCY_ROW = {
    pregnancy_number: "",
    year: "",
    gestational_age_at_delivery: "",
    mode_of_delivery: "",
    place_of_delivery: "",
    birth_weight: "",
    sex: "",
    outcome: "",
    complications: "",
};

const DELIVERY_MODE_OPTIONS = [
    { value: "svd", label: "SVD" },
    { value: "vacuum", label: "Vacuum" },
    { value: "forceps", label: "Forceps" },
    { value: "cesarean", label: "Cesarean Section" },
    { value: "breech", label: "Breech Delivery" },
];

const DELIVERY_PLACE_OPTIONS = [
    { value: "hospital", label: "Hospital" },
    { value: "health_center", label: "Health Centre" },
    { value: "home", label: "Home" },
    { value: "tba", label: "TBA" },
    { value: "other", label: "Other" },
];

const OUTCOME_OPTIONS = [
    { value: "alive", label: "Alive & Well" },
    { value: "alive_unhealthy", label: "Alive (Health Issues)" },
    { value: "neonatal_death", label: "Neonatal Death" },
    { value: "stillbirth", label: "Stillbirth" },
    { value: "miscarriage", label: "Miscarriage" },
    { value: "abortion", label: "Abortion" },
];

const SEX_OPTIONS = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
];

/**
 * Calculate Expected Date of Delivery using Naegele's Rule
 * EDD = LNMP + 280 days
 */
const calculateEDD = (lnmpDate) => {
    if (!lnmpDate) return "";
    const lnmp = new Date(lnmpDate);
    if (isNaN(lnmp.getTime())) return "";
    const edd = new Date(lnmp);
    edd.setDate(edd.getDate() + 280);
    return edd.toISOString().split("T")[0];
};

/**
 * Calculate Gestational Age from LNMP in weeks and days
 */
const calculateGestationalAge = (lnmpDate) => {
    if (!lnmpDate) return { weeks: 0, days: 0, totalDays: 0, display: "" };
    const lnmp = new Date(lnmpDate);
    const today = new Date();
    if (isNaN(lnmp.getTime()) || lnmp > today) return { weeks: 0, days: 0, totalDays: 0, display: "" };
    const diffMs = today - lnmp;
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(totalDays / 7);
    const days = totalDays % 7;
    return {
        weeks,
        days,
        totalDays,
        display: `${weeks} weeks ${days > 0 ? `${days} day${days > 1 ? "s" : ""}` : ""}`.trim(),
    };
};

/**
 * Get trimester from gestational age in weeks
 */
const getTrimester = (weeks) => {
    if (weeks <= 0) return "";
    if (weeks <= 13) return "1st Trimester";
    if (weeks <= 27) return "2nd Trimester";
    return "3rd Trimester";
};

/**
 * Hook to manage ANC Booking state, validation, and API calls.
 */
export const useANCBooking = (actions, patient_number, visit_id, user_roles_id) => {
    // ─── Form State ──────────────────────────────────────────────────
    const [bookingData, setBookingData] = useState({
        lnmp: "",
        edd: "",
        gravida: "",
        parity: "",
        height_cm: "",
        pre_pregnancy_weight_kg: "",
        blood_group: "",
        rhesus_factor: "",
        marital_status: "",
        education_level: "",
        occupation: "",
        next_of_kin_name: "",
        next_of_kin_phone: "",
        next_of_kin_relationship: "",
        allergies: "",
        other_medical_conditions: "",
        other_surgical_history: "",
        is_emergency: false,
        emergency_reason: "",
    });

    const [medicalHistory, setMedicalHistory] = useState(
        MEDICAL_HISTORY_CHECKLIST.reduce((acc, item) => ({ ...acc, [item.key]: false }), {})
    );

    const [surgicalHistory, setSurgicalHistory] = useState(
        SURGICAL_HISTORY_CHECKLIST.reduce((acc, item) => ({ ...acc, [item.key]: false }), {})
    );

    const [previousPregnancies, setPreviousPregnancies] = useState([]);

    // ─── UI State ────────────────────────────────────────────────────
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState("idle");
    const [existingBooking, setExistingBooking] = useState(null);
    const [patientDetails, setPatientDetails] = useState({});
    const [activeStep, setActiveStep] = useState(0);

    // ─── Computed Values ────────────────────────────────────────────
    const gestationalAge = useMemo(
        () => calculateGestationalAge(bookingData.lnmp),
        [bookingData.lnmp]
    );

    const trimester = useMemo(() => getTrimester(gestationalAge.weeks), [gestationalAge.weeks]);

    const edd = useMemo(() => calculateEDD(bookingData.lnmp), [bookingData.lnmp]);

    // Auto-set EDD when LNMP changes
    useEffect(() => {
        if (bookingData.lnmp) {
            setBookingData((prev) => ({ ...prev, edd }));
        }
    }, [edd, bookingData.lnmp]);

    // ─── Load Patient Details ───────────────────────────────────────
    useEffect(() => {
        if (!patient_number) return;
        axios
            .get(`${config.smsUrl}/cpatient/patient_update_form`, {
                params: { patient_number },
            })
            .then((res) => {
                const data = res.data || {};
                setPatientDetails(data);
                // Pre-fill blood group if patient already has one
                if (data.blood_type) {
                    setBookingData((prev) => ({
                        ...prev,
                        blood_group: data.blood_type,
                    }));
                }
            })
            .catch((err) => logoutErrorMessage(err, null, actions));
    }, [patient_number]);

    // ─── View/Edit Mode ──────────────────────────────────────────────
    const [isViewingDetails, setIsViewingDetails] = useState(false);

    const handleEditBooking = useCallback(() => setIsViewingDetails(false), []);

    // ─── Check for Existing Active Booking ──────────────────────────
    useEffect(() => {
        if (!patient_number) return;
        setLoading("checking");
        axios
            .get(`${config.smsUrl}/obgyn/anc/booking/patient/${patient_number}`)
            .then((res) => {
                const data = res.data;
                if (data && data.id) {
                    setExistingBooking(data);
                    populateFormFromBooking(data);
                    setIsViewingDetails(true);
                }
                setLoading("idle");
            })
            .catch(() => {
                // No existing booking — show the form
                setLoading("idle");
            });
    }, [patient_number]);

    // ─── Populate Form from Existing Booking ────────────────────────
    const populateFormFromBooking = (data) => {
        setBookingData({
            lnmp: data.lnmp || "",
            edd: data.edd || "",
            gravida: data.gravida || "",
            parity: data.parity || "",
            height_cm: data.height_cm || "",
            pre_pregnancy_weight_kg: data.pre_pregnancy_weight_kg || "",
            blood_group: data.blood_group || "",
            rhesus_factor: data.rhesus_factor || "",
            marital_status: data.marital_status || "",
            education_level: data.education_level || "",
            occupation: data.occupation || "",
            next_of_kin_name: data.next_of_kin_name || "",
            next_of_kin_phone: data.next_of_kin_phone || "",
            next_of_kin_relationship: data.next_of_kin_relationship || "",
            allergies: data.allergies || "",
            other_medical_conditions: data.other_medical_conditions || "",
            other_surgical_history: data.other_surgical_history || "",
            is_emergency: Boolean(data.is_emergency),
            emergency_reason: data.emergency_reason || "",
        });

        if (data.medical_history) {
            setMedicalHistory((prev) => ({ ...prev, ...data.medical_history }));
        }
        if (data.surgical_history) {
            setSurgicalHistory((prev) => ({ ...prev, ...data.surgical_history }));
        }
        if (data.previous_pregnancies && data.previous_pregnancies.length > 0) {
            setPreviousPregnancies(data.previous_pregnancies);
        }
    };

    // ─── Event Handlers ─────────────────────────────────────────────
    const handleChange = useCallback((event) => {
        const { name, value } = event.target;
        setBookingData((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleSelectChange = useCallback((name, selectedOption) => {
        setBookingData((prev) => ({
            ...prev,
            [name]: selectedOption ? selectedOption.value : "",
        }));
    }, []);

    const handleEmergencyToggle = useCallback(() => {
        setBookingData((prev) => ({
            ...prev,
            is_emergency: !prev.is_emergency,
            emergency_reason: !prev.is_emergency ? prev.emergency_reason : "",
        }));
    }, []);

    const handleMedicalHistoryChange = useCallback((key) => {
        setMedicalHistory((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleSurgicalHistoryChange = useCallback((key) => {
        setSurgicalHistory((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // ─── Previous Pregnancies Table Handlers ────────────────────────
    const handleAddPregnancy = useCallback(() => {
        setPreviousPregnancies((prev) => [
            ...prev,
            {
                ...EMPTY_PREGNANCY_ROW,
                pregnancy_number: prev.length + 1,
            },
        ]);
    }, []);

    const handleRemovePregnancy = useCallback((index) => {
        setPreviousPregnancies((prev) => {
            const updated = prev.filter((_, i) => i !== index);
            // Re-number pregnancies
            return updated.map((p, i) => ({ ...p, pregnancy_number: i + 1 }));
        });
    }, []);

    const handlePregnancyChange = useCallback((index, field, value) => {
        setPreviousPregnancies((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        );
    }, []);

    // Auto-adjust previous pregnancies table when gravida changes
    useEffect(() => {
        const gravida = parseInt(bookingData.gravida);
        if (isNaN(gravida) || gravida <= 1) {
            // Current pregnancy is the first; no previous pregnancies
            return;
        }
        const previousCount = gravida - 1; // Exclude current pregnancy
        if (previousPregnancies.length < previousCount) {
            // Add rows to match
            const newRows = Array.from(
                { length: previousCount - previousPregnancies.length },
                (_, i) => ({
                    ...EMPTY_PREGNANCY_ROW,
                    pregnancy_number: previousPregnancies.length + i + 1,
                })
            );
            setPreviousPregnancies((prev) => [...prev, ...newRows]);
        }
    }, [bookingData.gravida]);

    // ─── Validation ─────────────────────────────────────────────────
    const validateStep = (step) => {
        switch (step) {
            case 0: // Clinical Information
                return Boolean(bookingData.lnmp && bookingData.gravida && bookingData.parity);
            case 1: // Medical & Surgical History
                return true; // Optional step
            case 2: // Previous Pregnancies
                return true; // Optional step (validated by gravida > 1)
            case 3: // Next of Kin & Demographics
                return true; // Optional but recommended
            default:
                return true;
        }
    };

    const isFormValid = () => {
        return Boolean(bookingData.lnmp && bookingData.gravida && bookingData.parity);
    };

    // ─── Stepper Navigation ─────────────────────────────────────────
    const handleNext = () => {
        if (!validateStep(activeStep)) {
            setSubmitted(true);
            return;
        }
        setActiveStep((prev) => Math.min(prev + 1, 3));
    };
    const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));
    const handleStepClick = (step) => {
        // Only allow jumping ahead if the steps in between are valid
        if (step > activeStep) {
            for (let s = activeStep; s < step; s++) {
                if (!validateStep(s)) {
                    setSubmitted(true);
                    return;
                }
            }
        }
        setActiveStep(step);
    };

    // ─── Submit ─────────────────────────────────────────────────────
    const handleSubmit = useCallback(
        (event) => {
            if (event) event.preventDefault();
            setSubmitted(true);

            if (!isFormValid()) {
                actions.snackbarActions.snackError("Please fill in all required fields (LNMP, Gravida, Parity)");
                setActiveStep(0); // Go back to first step with required fields
                return;
            }

            setLoading("submitting");

            const payload = {
                patient_number,
                visit_id,
                user_roles_id,
                booking_date: dateConvert(),
                lnmp: bookingData.lnmp,
                edd: bookingData.edd,
                gestational_age_at_booking: gestationalAge.weeks,
                gravida: parseInt(bookingData.gravida),
                parity: parseInt(bookingData.parity),
                height_cm: bookingData.height_cm ? parseFloat(bookingData.height_cm) : null,
                pre_pregnancy_weight_kg: bookingData.pre_pregnancy_weight_kg
                    ? parseFloat(bookingData.pre_pregnancy_weight_kg)
                    : null,
                blood_group: bookingData.blood_group,
                rhesus_factor: bookingData.rhesus_factor,
                marital_status: bookingData.marital_status,
                education_level: bookingData.education_level,
                occupation: bookingData.occupation,
                next_of_kin_name: bookingData.next_of_kin_name,
                next_of_kin_phone: bookingData.next_of_kin_phone,
                next_of_kin_relationship: bookingData.next_of_kin_relationship,
                allergies: bookingData.allergies,
                medical_history: medicalHistory,
                surgical_history: surgicalHistory,
                other_medical_conditions: bookingData.other_medical_conditions,
                other_surgical_history: bookingData.other_surgical_history,
                previous_pregnancies: previousPregnancies,
                is_emergency: bookingData.is_emergency,
                emergency_reason: bookingData.is_emergency ? bookingData.emergency_reason : null,
            };

            const request = existingBooking
                ? axios.put(
                      `${config.smsUrl}/obgyn/anc/booking/${existingBooking.id}`,
                      payload
                  )
                : axios.post(`${config.smsUrl}/obgyn/anc/booking`, payload);

            request
                .then((res) => {
                    setLoading("success");
                    const booking = res.data?.data || res.data;
                    setExistingBooking(booking);
                    setIsViewingDetails(true);
                    actions.snackbarActions.snackSuccess(
                        existingBooking
                            ? "ANC booking updated successfully"
                            : "ANC booking created successfully"
                    );
                })
                .catch((err) => {
                    setLoading("error");
                    logoutErrorMessage(err, null, actions);
                });
        },
        [
            bookingData,
            medicalHistory,
            surgicalHistory,
            previousPregnancies,
            patient_number,
            visit_id,
            user_roles_id,
            existingBooking,
            gestationalAge,
            actions,
        ]
    );

    // ─── Return Values ──────────────────────────────────────────────
    return {
        // Form data
        bookingData,
        medicalHistory,
        surgicalHistory,
        previousPregnancies,

        // Patient info
        patientDetails,
        existingBooking,

        // Computed
        gestationalAge,
        trimester,

        // UI state
        submitted,
        loading,
        activeStep,

        // View/edit mode
        isViewingDetails,
        handleEditBooking,

        // Handlers
        handleChange,
        handleSelectChange,
        handleEmergencyToggle,
        handleMedicalHistoryChange,
        handleSurgicalHistoryChange,
        handleAddPregnancy,
        handleRemovePregnancy,
        handlePregnancyChange,
        handleNext,
        handleBack,
        handleStepClick,
        handleSubmit,

        // Validation
        validateStep,
        isFormValid,

        // Constants for rendering
        BLOOD_GROUP_OPTIONS,
        MARITAL_STATUS_OPTIONS,
        EDUCATION_LEVEL_OPTIONS,
        RELATIONSHIP_OPTIONS,
        MEDICAL_HISTORY_CHECKLIST,
        SURGICAL_HISTORY_CHECKLIST,
        DELIVERY_MODE_OPTIONS,
        DELIVERY_PLACE_OPTIONS,
        OUTCOME_OPTIONS,
        SEX_OPTIONS,
    };
};

// Export utility functions for use in other components
export { calculateEDD, calculateGestationalAge, getTrimester };
